import { isObject } from 'lodash-es';
import { BoolStr, Nil } from '../definition';

export function joinStrings(...params: (string | undefined)[]) {
  const sep = params.pop();
  return params.filter(Boolean).join(sep);
}

export function boolStr(v?: boolean): BoolStr {
  return v ? 'true' : 'false';
}

export function stripSlashesFromJson(value?: any) {
  if (typeof value === 'string')
    value = value.replace(/\\+"/g, '"').replace(/^"+/, '').replace(/"+$/, '');
  return value;
}

export function jsonParse<T = any, U = any | null>(
  value?: any,
  defaultValue?: U
): T | U {
  if (isObject(value)) return value as T;

  try {
    return JSON.parse(stripSlashesFromJson(value)) as T;
  } catch {
    if (typeof value === 'string') return stripSlashesFromJson(value) as T;
    return (defaultValue ?? null) as U;
  }
}

/**
 * Like `jsonParse`, but does NOT corrupt already-valid JSON.
 *
 * `jsonParse` runs `stripSlashesFromJson` (which blindly turns every `\"` into
 * `"`) BEFORE `JSON.parse`. For a value that legitimately contains escaped quote
 * characters — e.g. a saved-search whose keyword values are quoted phrases like
 * `"supply chain"^"Procurement"` — that stripping breaks otherwise-valid JSON
 * (`{"k":"\"supply chain\""}` -> `{"k":""supply chain""}`), `JSON.parse` throws,
 * and the catch returns the raw string. Downstream that string gets spread into a
 * char-indexed object and the whole preset silently loads with no filters
 * applied (CU: quoted-keyword saved search opens empty).
 *
 * This variant parses the raw value first and only falls back to slash-stripping
 * when the raw parse fails (the legacy double-escaped shape). Prefer it wherever
 * the input is expected to be well-formed JSON (e.g. preset `value` payloads).
 */
export function jsonParseSafe<T = any, U = any | null>(
  value?: any,
  defaultValue?: U
): T | U {
  if (isObject(value)) return value as T;

  try {
    return JSON.parse(value) as T;
  } catch {
    try {
      return JSON.parse(stripSlashesFromJson(value)) as T;
    } catch {
      if (typeof value === 'string') return stripSlashesFromJson(value) as T;
      return (defaultValue ?? null) as U;
    }
  }
}

export function isValidJsonString(value: any) {
  return typeof value === 'string' && !!jsonParse(value);
}

export function jsonStringify(value?: any, indent = 0): string | null {
  try {
    return isValidJsonString(stripSlashesFromJson(value))
      ? stripSlashesFromJson(value)
      : JSON.stringify(value, undefined, indent);
  } catch {
    return null;
  }
}

export function formatString(template: string, ...args: string[]) {
  return template.replace(/{([0-9]+)}/g, (match, index) => {
    return typeof args[index] === 'undefined' ? match : args[index];
  });
}

export function searchText(value: string, keyword: string) {
  return value.search(new RegExp(keyword, 'i'));
}

export function capitalize(value: Nil<string>) {
  if (!value) return '';
  return value
    ?.toLowerCase()
    .replace(/[-_]/g, ' ')
    .replace(/\b[a-z]/g, (letter) => {
      return letter.toUpperCase();
    });
}

export function stringIncludes(
  value: Nil<string>,
  needle: Nil<string>,
  caseSensitive: boolean = false
) {
  if (!needle || !needle?.length) return true;
  if (!caseSensitive)
    return value?.trim().toLowerCase().includes(needle.trim().toLowerCase());
  return value?.trim().includes(needle.trim());
}

export function stringEquals(
  value: Nil<string>,
  needle: Nil<string>,
  caseSensitive: boolean = false
) {
  if (!needle) return false;
  if (!caseSensitive)
    return value?.trim().toLowerCase() === needle.trim().toLowerCase();
  return value?.trim() === needle.trim();
}

export function maskText(value: string, startMaskCount = 6, endMaskCount = 6) {
  if (value.length <= startMaskCount + endMaskCount)
    return '*'.repeat(value.length); // avoid negative slice
  const first = value.slice(0, startMaskCount);
  const last = value.slice(-endMaskCount);
  const masked = '*'.repeat(value.length - (startMaskCount + endMaskCount));
  return `${first}${masked}${last}`;
}

export function trimText(value: string, maxLength: number, suffix = '...') {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}${suffix}`;
}
